import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Space, message } from 'antd';
import { ConsignmentItem, ConsignmentVoidBatchPayload } from '../../model/consignment';
import { BATCH_REASON_MAX_CHARS, isStaleBatchError } from './consignment-batch-edit-modal';

const genKey = (): string =>
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `void-batch-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface Props {
    open: boolean;
    item: ConsignmentItem | null;
    expectedPeriodId?: number;
    onClose: () => void;
    onVoid: (payload: ConsignmentVoidBatchPayload) => Promise<any>;
    onStale?: () => void;
}

/** "ลบ/ยกเลิกรายการ": a lot that should never have been entered. Not a
 * delete -- the lot stays in the bill's history as "ยกเลิกแล้ว" -- and not a
 * return or a sale: nothing is owed and nothing is earned. A reason is
 * required before the button does anything. */
export const ConsignmentBatchVoidModal: React.FC<Props> = ({ open, item, expectedPeriodId, onClose, onVoid, onStale }) => {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState('');

    useEffect(() => {
        if (!open || !item) return;
        setReason('');
        setIdempotencyKey(genKey());
        // Reset per lot, not on every re-render of the same lot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, item?.id]);

    if (!item) return null;

    const trimmed = reason.trim();

    const handleVoid = async () => {
        if (!trimmed) {
            message.error('กรุณาระบุเหตุผลที่ยกเลิกล็อตนี้');
            return;
        }
        if (expectedPeriodId === undefined || item.id === undefined) {
            message.error('ไม่พบรอบบิลที่เปิดอยู่ กรุณารีเฟรชข้อมูล');
            return;
        }
        setSaving(true);
        try {
            await onVoid({
                batch_id: item.id,
                expected_period_id: expectedPeriodId,
                expected_quantity_received: Number(item.quantity_received),
                expected_selling_price_per_unit: Number(item.selling_price_per_unit),
                expected_owner_cost_per_unit: Number(item.owner_cost_per_unit),
                reason: trimmed,
                idempotency_key: idempotencyKey
            });
            message.success(`ยกเลิกล็อตที่ ${item.batch_no} เรียบร้อย`);
            onClose();
        } catch (error: any) {
            const msg: string = error?.message || 'ยกเลิกล็อตไม่สำเร็จ';
            message.error(msg);
            if (isStaleBatchError(msg)) {
                onClose();
                onStale?.();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            className="consignment-batch-void-modal"
            open={open}
            title="🗑️ ยกเลิกรายการล็อตนี้?"
            okText="ยืนยันยกเลิกล็อต"
            cancelText="ไม่ยกเลิก"
            confirmLoading={saving}
            onOk={handleVoid}
            onCancel={saving ? undefined : onClose}
            okButtonProps={{ danger: true, disabled: saving || !trimmed, 'data-testid': 'btn-confirm-batch-void' } as any}
            destroyOnClose
            width={520}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div className="consignment-batch-context" data-testid="batch-void-context">
                    <div><span>สินค้า</span><strong>{item.product_name}{item.product_code ? ` (${item.product_code})` : ''}</strong></div>
                    <div><span>ล็อต</span><strong>ที่ {item.batch_no ?? '-'}</strong></div>
                    <div><span>จำนวนรับฝาก</span><strong>{item.quantity_received} {item.product_unit_name || ''}</strong></div>
                </div>
                <Alert
                    type="warning"
                    showIcon
                    message="ใช้เมื่อกรอก/เลือกสินค้าผิดเท่านั้น"
                    description="ระบบจะเก็บประวัติไว้ แต่จะไม่นับล็อตนี้ในสต๊อกและยอดรวม ถ้าเป็นการคืนของให้ผู้ฝากให้ใช้ “คืนผู้ฝาก” แทน"
                />
                <label className="consignment-batch-field">
                    <span>เหตุผลที่ยกเลิก (ต้องระบุ)</span>
                    <Input.TextArea rows={2} maxLength={BATCH_REASON_MAX_CHARS} showCount value={reason}
                                    placeholder="เช่น เลือกสินค้าผิดตัว, กรอกซ้ำ"
                                    onChange={e => setReason(e.target.value)} data-testid="input-batch-void-reason" />
                </label>
            </Space>
        </Modal>
    );
};

export default ConsignmentBatchVoidModal;
